import {BulkArchiveItemsResponse, BulkUnarchiveItemsResponse} from 'archive/ArchiveConstants';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import {Section} from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {ActionOrigin, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {optimisticallyUpdate} from 'lib/OptimisticallyUpdate';
import {
  ApplyStaticTemplateParams,
  AddAndRunStepsParams,
  AddAndRunStepsParamsV2,
  previewStep,
  PreviewOutput,
  PreviewStepParams,
  AddOrReplaceAndRunStepParams,
  UpdateAndRunStepParams,
  ReplaceOptions,
  DeletePipelineStepsBehavior,
} from 'pipelines/PipelineDAO';
import * as PipelineDAO from 'pipelines/PipelineDAO';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {getStore} from 'redux-stores/Store';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import {AsyncState, makeAsyncCall} from 'ui-library/hooks/useAsyncState';
import * as TaskRunner from 'util/TaskRunner';

import * as AnalysisDAO from './AnalysisDAO';
import {
  CloneAnalysisParams,
  CloneAnalysisResponse,
  CreateAnalysisParams,
  CreateAnalysisResponse,
  UpdateAnalysisParams,
} from './AnalysisDAO';
import {TRANSFORMATION_STEP_TYPES} from './AnalysisToolConstants';
import {isAnalysisToolRedesignEnabled} from './AnalysisToolFeatureGates';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from './AnalysisTypes';
import getDownstreamSteps from './data/getDownstreamSteps';
import getLegacyStepFromAnalysis, {getStepFromAnalysis} from './data/getStepFromAnalysis';
import {AnalysisFragment} from './graphql/GetAnalysis';
import refreshAnalysis from './refreshAnalysis';
import {updateSelectedRightSidebarItemForDelete} from './ui/hooks/useRightSidebarItem';
import {updateSelectedAnalysisItemForDelete} from './ui/hooks/useSelectedAnalysisItem';

function forceRefreshSearch(): void {
  getStore().dispatch(SearchUIReduxActions.forceRefreshSearch(Section.ANALYTICS));
  getStore().dispatch(SearchUIReduxActions.forceRefreshSearch(Section.SEARCH));
}

function handleCreateAnalysisBackgroundProgressToast(taskId: string, response: CreateAnalysisResponse): void {
  const successTrayTask = {
    progress: TrayTaskConstants.SUCCESS_PROGRESS,
    subheading: 'Created analysis',
    onSubheadingClick: () => {
      FileBrowserActions.openFile(response.analysisId);
    },
  };
  // If background task for seeding data was actually returned, wait for it to finish before updating toast
  if (response.seedAnalysisTask != null) {
    TaskRunner.resumeTaskPromise(response.seedAnalysisTask.status_url)
      .then(() => {
        TrayTaskActions.updateTrayTask(taskId, successTrayTask);
      })
      .catch(() => {
        TrayTaskActions.updateTrayTask(taskId, {
          progress: TrayTaskConstants.FAILURE_PROGRESS,
          error: 'An error occurred while creating the analysis tables.',
          actions: [
            {
              actionText: 'Open',
              onActionClick: () => {
                FileBrowserActions.openFile(response.analysisId);
              },
            },
          ],
        });
      });
  } else {
    // Otherwise, immediately update with success
    TrayTaskActions.updateTrayTask(taskId, successTrayTask);
  }
}

/**
 * Create analysis.
 *
 * @param data Data for create analysis
 * @param _ Unused parameter needed for type compatibility with creating analysis from table.
 * @param withBackgroundSeedData Whether to seed data in the background (progress toast) rather than
 * navigating to the analysis. Defaults to false.
 */
export async function createAnalysis(
  data: CreateAnalysisParams,
  _?: string,
  withBackgroundSeedData: boolean = false
): Promise<CreateAnalysisResponse> {
  let taskId: string | null = null;
  if (withBackgroundSeedData) {
    taskId = TrayTaskActions.createTrayTask({
      heading: 'Creating analysis',
      progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
    });
  }
  const response = await AnalysisDAO.createAnalysis(data);

  // With no background seeding of data, just open analysis
  if (taskId == null) {
    FileBrowserActions.openFile(response.analysisId);
  } else {
    handleCreateAnalysisBackgroundProgressToast(taskId, response);
  }
  forceRefreshSearch();
  return response;
}

function writeAnalysisToCache(analysis: AnalysisGraphQL): void {
  writeFragmentsToCache(AnalysisFragment, [analysis]);
}

export async function updateAnalysisOptimistically(
  analysis: AnalysisGraphQL,
  updateAnalysisParams: UpdateAnalysisParams
): Promise<void> {
  await optimisticallyUpdate<AnalysisGraphQL, AnalysisGraphQL>({
    persistData: () =>
      AnalysisDAO.updateAnalysis(analysis.id, updateAnalysisParams).then(() => refreshAnalysis(analysis.id)),
    writeDataToCache: writeAnalysisToCache,
    optimisticData: {
      // We write the whole analysis to the cache because writing a partial fragment (i.e. just the new name
      // and/or project) will not trigger the UI to rerender with the changed cache
      ...analysis,
      ...updateAnalysisParams,
    },
    revertData: analysis,
  });
}

export async function cloneAnalysis(
  analysisId: string,
  cloneAnalysisParams: CloneAnalysisParams
): Promise<CloneAnalysisResponse> {
  const response = await AnalysisDAO.cloneAnalysis(analysisId, cloneAnalysisParams);
  FileBrowserActions.openFile(response.newAnalysisId);
  forceRefreshSearch();
  return response;
}

export async function copyAnalysisSteps(analysisId: string, stepIds: Array<string>): Promise<void> {
  await PipelineDAO.copyPipelineSteps(analysisId, {stepIds});
  await refreshAnalysis(analysisId);
}

export async function copyAnalysisViewSteps(analysisId: string, viewStepId: string): Promise<void> {
  await PipelineDAO.copyPipelineView(analysisId, {viewStepId});
  await refreshAnalysis(analysisId);
}

export async function addAndRunAnalysisSteps(
  analysisId: string,
  data: AddAndRunStepsParams
): Promise<Array<GetAnalysisPipelineStep>> {
  const {stepIds} = await PipelineDAO.addAndRunPipelineSteps(analysisId, data);
  const updatedAnalysis = await refreshAnalysis(analysisId);
  return stepIds.map((stepId) => getLegacyStepFromAnalysis(updatedAnalysis, stepId));
}

export async function addAndRunAnalysisStepsV2(
  analysisId: string,
  data: AddAndRunStepsParamsV2
): Promise<Array<GetAnalysisPipelineStep>> {
  const {stepIds} = await PipelineDAO.addAndRunPipelineStepsV2(analysisId, data);
  const updatedAnalysis = await refreshAnalysis(analysisId);
  return stepIds.map((stepId) => getLegacyStepFromAnalysis(updatedAnalysis, stepId));
}

/**
 * Return the steps that we expect to be deleted by the given stepIds and behavior.
 *
 * This function doesn't account for validation, so it might have more steps than the actual deletion if the
 * backend rejects the deletion of some steps. That is an acceptable tradeoff for the use with front-end
 * state management where underestimating the number of deleted steps would be worse (since we'd select a
 * view that will cause a crash).
 *
 * @returns Set of step ids that will be deleted based on the given behavior.
 * @package
 */
export function getStepsToBeDeleted(
  analysis: AnalysisGraphQL,
  stepIds: Array<string>,
  behavior: DeletePipelineStepsBehavior
): Set<string> {
  const stepsToDelete = new Set(stepIds);

  switch (behavior) {
    case DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN: {
      for (const stepId of stepIds) {
        const downstreamSteps = getDownstreamSteps(analysis, stepId);
        for (const step of downstreamSteps) {
          stepsToDelete.add(step.id);
        }
      }
      break;
    }
    case DeletePipelineStepsBehavior.DELETE_ALL_CHILD_TRANSFORMATION_STEPS: {
      for (const stepId of stepIds) {
        const downstreamSteps = getDownstreamSteps(analysis, stepId);
        for (const step of downstreamSteps) {
          if (TRANSFORMATION_STEP_TYPES.includes(step.definition.stepType)) {
            stepsToDelete.add(step.id);
          }
        }
      }
      break;
    }
    case DeletePipelineStepsBehavior.NO_INFERENCE:
    case DeletePipelineStepsBehavior.NO_INFERENCE_WITH_REPARENTING:
    default: {
      break;
    }
  }

  return stepsToDelete;
}

/**
 * Deletes analysis steps and updates the selected item and right sidebar item is those become invalid.
 */
export async function deleteAnalysisSteps(
  analysis: AnalysisGraphQL,
  data: {
    stepIds: Array<string>;
    behavior: DeletePipelineStepsBehavior;
  },
  clientStateId: string
): Promise<void> {
  if (isAnalysisToolRedesignEnabled()) {
    const stepsToDelete = getStepsToBeDeleted(analysis, data.stepIds, data.behavior);

    await updateSelectedAnalysisItemForDelete(
      async () => {
        await updateSelectedRightSidebarItemForDelete(
          async () => {
            await PipelineDAO.deletePipelineSteps(analysis.id, data);
            await refreshAnalysis(analysis.id);
          },
          analysis,
          clientStateId,
          stepsToDelete
        );
      },
      analysis,
      clientStateId,
      stepsToDelete
    );
  } else {
    await PipelineDAO.deletePipelineSteps(analysis.id, data);
    await refreshAnalysis(analysis.id);
  }
}

export async function addOrReplaceAndRunAnalysisStep(
  analysisId: string,
  data: AddOrReplaceAndRunStepParams,
  replaceOptions?: ReplaceOptions
): Promise<GetAnalysisPipelineStep> {
  let result;
  if (!replaceOptions) {
    result = await PipelineDAO.addAndRunPipelineStep(analysisId, data);
  } else {
    result = await PipelineDAO.replaceAndRunPipelineStep(analysisId, data, replaceOptions);
  }

  const updatedAnalysis = await refreshAnalysis(analysisId);
  return getLegacyStepFromAnalysis(updatedAnalysis, result.stepId);
}

export async function updateAnalysisStep(
  analysisId: string,
  data: UpdateAndRunStepParams
): Promise<GetAnalysisPipelineStep> {
  await PipelineDAO.updatePipelineStep(analysisId, data);
  const updatedAnalysis = await refreshAnalysis(analysisId);
  return getLegacyStepFromAnalysis(updatedAnalysis, data.stepId);
}

export async function updateAndRunAnalysisStep(
  analysisId: string,
  data: UpdateAndRunStepParams
): Promise<GetAnalysisPipelineStep> {
  await PipelineDAO.updateAndRunPipelineStep(analysisId, data);
  const updatedAnalysis = await refreshAnalysis(analysisId);
  return getLegacyStepFromAnalysis(updatedAnalysis, data.stepId);
}

export async function runAnalysisStep(
  analysisId: string,
  data: {stepId: string}
): Promise<GetAnalysisPipelineStep> {
  await PipelineDAO.runPipelineStep(analysisId, data);
  const updatedAnalysis = await refreshAnalysis(analysisId);
  return getLegacyStepFromAnalysis(updatedAnalysis, data.stepId);
}

export function runPreviewAnalysisStep<StepType extends PipelineStepType>(
  data: PreviewStepParams<StepType>,
  currentState: AsyncState<PreviewOutput<StepType>>,
  setAsyncState: (nextState: AsyncState<PreviewOutput<StepType>>) => void
): void {
  makeAsyncCall({
    makeCall: previewStep,
    args: [data],
    asyncState: currentState,
    setAsyncState,
  });
}

export async function archiveAnalysis(
  analysisId: string,
  purpose: string,
  resultPurpose: string | null
): Promise<BulkArchiveItemsResponse> {
  let response: BulkArchiveItemsResponse;
  await callAsyncWithDataModifications({
    changeSummary: '1 analysis will be archived',
    onPerformAction: async (dataModsHeader?) => {
      response = await PipelineDAO.bulkArchivePipelines([analysisId], purpose, resultPurpose, dataModsHeader);
    },
    apiIds: [analysisId],
    analyticsParams: {
      module: 'ANALYSIS_TOOL',
      actionOrigin: ActionOrigin.ANALYSIS_TOOL,
      modifiedAction: 'MODIFY',
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: true,
  });

  await refreshAnalysis(analysisId);
  forceRefreshSearch();
  return response!;
}

export async function unarchiveAnalysis(analysisId: string): Promise<BulkUnarchiveItemsResponse> {
  let response: BulkUnarchiveItemsResponse;
  await callAsyncWithDataModifications({
    changeSummary: '1 analysis will be unarchived',
    onPerformAction: async (dataModsHeader?) => {
      response = await PipelineDAO.bulkUnarchivePipelines([analysisId], dataModsHeader);
    },
    apiIds: [analysisId],
    analyticsParams: {
      module: 'ANALYSIS_TOOL',
      actionOrigin: ActionOrigin.ANALYSIS_TOOL,
      modifiedAction: 'MODIFY',
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: true,
  });

  await refreshAnalysis(analysisId);
  forceRefreshSearch();
  return response!;
}

export async function applyStaticTemplate(
  analysisId: string,
  data: ApplyStaticTemplateParams
): Promise<Array<PipelineStep>> {
  const {newStepIds} = await PipelineDAO.applyStaticTemplate(analysisId, data);
  const updatedAnalysis = await refreshAnalysis(analysisId);
  return newStepIds.map((stepId) => getStepFromAnalysis(updatedAnalysis, stepId));
}
